import * as coreEntities from 'core/store/index';
import { isStudent } from 'core/utils/user';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { detectConversationMode } from '../../../contexts/AIChatConversation/aiChatConversationReducer';
import { useAIChatConversation } from '../../../contexts/AIChatConversation/useAIChatConversation';
import { AI_CHAT_MAX_WIDTH_PERCENTAGE, AI_CHAT_MIN_WIDTH } from '../../../contexts/AIChatShell/aiChatShellReducer';
import { useAIChatShell } from '../../../contexts/AIChatShell/useAIChatShell';
import { createChatService } from '../../../services/chatService';
import ChatTriggerButton from '../../atoms/ChatTriggerButton/ChatTriggerButton';
import ChatbotResizeHandle from '../../atoms/ChatbotResizeHandle/ChatbotResizeHandle';
import Chatbot from '../Chatbot/Chatbot';

/**
 * Core chatbot widget component that uses the Context and manages UI state
 */
function ChatbotWidgetCore({
  chatService,
  historyButtonLabel,
  buttonIconName,
  errorMessage,
  className,
  children,
  getChatContextData,
  animationDuration,
  onOpen,
  onClose,
  isControlled,
  isOpenControlled,
  focusOnOpen,
  hideFloatingButton,
}) {
  const { t } = useTranslation(['chatbot']);
  const location = useLocation();
  const { isChatbotOpen, openChatbot, closeChatbot, chatbotWidth, setChatbotWidth } = useAIChatShell();
  const { mode, setConversationMode } = useAIChatConversation();

  // Get current user from Redux store
  const currentUser = useSelector((state) => coreEntities.auth.selectors.getUser(state));

  // Local UI state
  const [isOpen, setIsOpen] = useState(isOpenControlled || false);
  const [isClosing, setIsClosing] = useState(false);
  const [isButtonHidden, setIsButtonHidden] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  // Refs
  const closeTimeoutRef = useRef(null);
  const sidebarRef = useRef(null);
  const inputRef = useRef(null);
  // Drag-resize scratch state: track the live width locally (ref, no render) and the active
  // document listeners so an unmount mid-drag can detach them and still commit the final width.
  const latestWidthRef = useRef(null);
  const hasDraggedRef = useRef(false);
  const resizeMoveRef = useRef(null);
  const resizeUpRef = useRef(null);
  const defaultChatService = useMemo(() => createChatService(), []);

  // Validate user permissions - only show for students with AI enabled
  if (!currentUser?.is_tangerine_ai || !isStudent(currentUser)) {
    return children || null;
  }

  // Handlers
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setIsButtonHidden(false);

    if (!isControlled) {
      closeChatbot();
    }

    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      onClose?.();
    }, animationDuration);
  }, [animationDuration, onClose, closeChatbot, isControlled]);

  const handleOpen = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setIsButtonHidden(true);
    setIsClosing(false);
    setIsOpen(true);

    if (!isControlled) {
      openChatbot();
    }

    onOpen?.();
  }, [onOpen, openChatbot, isControlled]);

  // Calculate max width based on viewport
  const getMaxWidth = useCallback(() => {
    return window.innerWidth * AI_CHAT_MAX_WIDTH_PERCENTAGE;
  }, []);

  // Handle resize start
  const handleResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startWidth = chatbotWidth;

      hasDraggedRef.current = false;
      latestWidthRef.current = startWidth;

      const handleMouseMove = (moveEvent) => {
        const deltaX = startX - moveEvent.clientX; // Negative because we're dragging from the left edge
        const newWidth = Math.max(AI_CHAT_MIN_WIDTH, Math.min(getMaxWidth(), startWidth + deltaX));
        hasDraggedRef.current = true;
        latestWidthRef.current = newWidth;
        // Render locally: paint the frame straight onto the CSS var instead of dispatching into the
        // Shell slice every mousemove (which re-rendered every Shell subscriber and thrashed persistence).
        document.documentElement.style.setProperty('--chatbot-width', `${newWidth}px`);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        resizeMoveRef.current = null;
        resizeUpRef.current = null;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        // Commit once on release so only the final width churns the Shell slice (which then re-sets
        // the same CSS var idempotently and becomes source of truth again). Reset the drag flag so
        // a later unmount doesn't re-commit this now-stale width over external changes.
        if (hasDraggedRef.current) {
          setChatbotWidth(latestWidthRef.current);
          hasDraggedRef.current = false;
        }
      };

      resizeMoveRef.current = handleMouseMove;
      resizeUpRef.current = handleMouseUp;
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    [chatbotWidth, setChatbotWidth, getMaxWidth]
  );

  // Effects
  useEffect(() => {
    if (!isControlled && isChatbotOpen !== isOpen) {
      if (isChatbotOpen) {
        handleOpen();
      } else {
        handleClose();
      }
    }
  }, [isChatbotOpen, isOpen, isControlled, handleOpen, handleClose]);

  useEffect(() => {
    if (isControlled && typeof isOpenControlled !== 'undefined') {
      setIsOpen(isOpenControlled);
      setIsClosing(false);
    }
  }, [isControlled, isOpenControlled]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      // Unmounted mid-drag: detach the leaked document listeners, restore the global body styles
      // handleResizeStart set (handleMouseUp never ran to reset them), and commit the final width.
      if (resizeMoveRef.current) {
        document.removeEventListener('mousemove', resizeMoveRef.current);
      }
      if (resizeUpRef.current) {
        document.removeEventListener('mouseup', resizeUpRef.current);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      }
      if (hasDraggedRef.current && latestWidthRef.current != null) {
        setChatbotWidth(latestWidthRef.current);
      }
    };
  }, [setChatbotWidth]);

  useEffect(() => {
    if (isOpen && !isClosing && focusOnOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen, isClosing, focusOnOpen]);

  // Verify mode matches current URL when chatbot opens
  useEffect(() => {
    if (isOpen && !isClosing) {
      const currentMode = detectConversationMode(window.location.pathname);
      if (mode !== currentMode) {
        setConversationMode(currentMode);
      }
    }
  }, [isOpen, isClosing, mode, setConversationMode]);

  // Update CSS variable when width changes
  useEffect(() => {
    document.documentElement.style.setProperty('--chatbot-width', `${chatbotWidth}px`);
  }, [chatbotWidth]);

  // Handle window resize to enforce max width constraint
  useEffect(() => {
    const handleWindowResize = () => {
      const newMaxWidth = getMaxWidth();
      if (chatbotWidth > newMaxWidth) {
        setChatbotWidth(newMaxWidth);
      }
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [chatbotWidth, setChatbotWidth, getMaxWidth]);

  // Monitor route changes and update mode
  useEffect(() => {
    const detectedMode = detectConversationMode(location.pathname);

    // Update mode if it changed
    if (mode !== detectedMode) {
      setConversationMode(detectedMode);

      // Close chatbot on mode change while open
      if (isChatbotOpen) {
        handleClose();
      }
    }
  }, [location.pathname, mode, isChatbotOpen, handleClose, setConversationMode]);

  // Chat props
  const chatbotProps = useMemo(
    () => ({
      chatService: chatService || defaultChatService,
      historyButtonLabel,
      errorMessage,
      onClose: handleClose,
      isVisible: isOpen && !isClosing,
      getChatContextData:
        getChatContextData ||
        (() => ({
          currentPage: window.location.pathname,
          timestamp: new Date().toISOString(),
        })),
      inputRef,
    }),
    [chatService, defaultChatService, historyButtonLabel, errorMessage, handleClose, isOpen, isClosing, getChatContextData]
  );

  return (
    <div
      className={`base-chatbot ${isOpen ? 'base-chatbot--enabled' : ''} ${isClosing ? 'chatbot-closing' : ''} ${isResizing ? 'base-chatbot--resizing' : ''}`}
      role="region"
      aria-label={t('Chat assistant')}
    >
      <div className="base-chatbot__container">{children}</div>

      <div className="base-chatbot__chatbot" ref={sidebarRef} tabIndex={isOpen && !isClosing ? 0 : -1} aria-hidden={!isOpen || isClosing}>
        {isOpen && <ChatbotResizeHandle onResize={handleResizeStart} isResizing={isResizing} />}
        <Chatbot {...chatbotProps} className={className} buttonIconName={buttonIconName} />
      </div>

      {!hideFloatingButton && (
        <div className={`base-chatbot__button-container ${isButtonHidden ? 'base-chatbot__button-container--hidden' : ''}`} aria-hidden={isButtonHidden}>
          <ChatTriggerButton onClick={handleOpen} aria-label={t('Open chat assistant')} aria-expanded={isOpen && !isClosing} />
        </div>
      )}

      {isResizing && <div className="base-chatbot__resize-overlay" />}
    </div>
  );
}

ChatbotWidgetCore.propTypes = {
  chatService: PropTypes.object,
  historyButtonLabel: PropTypes.string,
  buttonIconName: PropTypes.string,
  errorMessage: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  getChatContextData: PropTypes.func,
  animationDuration: PropTypes.number,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  isControlled: PropTypes.bool,
  isOpenControlled: PropTypes.bool,
  focusOnOpen: PropTypes.bool,
  hideFloatingButton: PropTypes.bool,
};

export default ChatbotWidgetCore;
